import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Gavel, Clock, Users, FileText, ArrowRight, 
  Calendar, MapPin, TrendingUp, Shield, CheckCircle,
  ChevronRight
} from 'lucide-react';

export default function EncheresHub() {
  const { API } = useContext(AppContext);
  const [vacations, setVacations] = useState([]);
  const [pastVacations, setPastVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seoContent, setSeoContent] = useState(null);

  useEffect(() => {
    fetchVacations();
    fetchSeoContent();
  }, []);

  const fetchVacations = async () => {
    try {
      // Current/upcoming vacations
      const response = await axios.get(`${API}/encheres/vacations`);
      setVacations(response.data || []);
      
      // Past vacations for reference
      const pastResponse = await axios.get(`${API}/encheres/vacations?include_past=true`);
      const past = (pastResponse.data || []).filter(v => v.statut === 'attribuee');
      setPastVacations(past.slice(0, 6));
    } catch (error) {
      console.error('Error fetching vacations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeoContent = async () => {
    try {
      const response = await axios.get(`${API}/encheres/conditions`);
      setSeoContent(response.data?.seo_texte_fr);
    } catch (error) {
      console.error('Error fetching SEO content:', error);
    }
  };

  const getStatusBadge = (statut) => {
    const configs = {
      annoncee: { label: 'Inscriptions ouvertes', color: 'bg-green-500', icon: Users },
      inscriptions_closes: { label: 'Inscriptions closes', color: 'bg-yellow-500', icon: Clock },
      vente_en_cours: { label: 'Enchères en cours', color: 'bg-[#F0A22E]', icon: Gavel },
      close: { label: 'Enchères closes', color: 'bg-gray-500', icon: Clock },
      attribuee: { label: 'Vente terminée', color: 'bg-blue-500', icon: CheckCircle }
    };
    const config = configs[statut] || { label: statut, color: 'bg-gray-400', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Schema.org data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Vente aux enchères de matériel géotechnique et fondations spéciales",
    "description": "Foreuses, sondeuses, enregistreurs de forage et outillage vendus par courtage aux enchères. Inscription gratuite, dépôt de lots sans droit d'entrée.",
    "url": "https://geots.consulting/encheres",
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="encheres-hub-page">
      <Helmet>
        <title>Vente aux enchères de matériel géotechnique et fondations spéciales | GEOTS</title>
        <meta 
          name="description" 
          content="Foreuses, sondeuses, enregistreurs de forage et outillage vendus par courtage aux enchères. Inscription gratuite, dépôt de lots sans droit d'entrée." 
        />
        <link rel="canonical" href="https://geots.consulting/encheres" />
        <meta property="og:title" content="Enchères Matériel Géotechnique | GEOTS" />
        <meta property="og:description" content="Foreuses, sondeuses et outillage vendus par courtage aux enchères." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://geots.consulting/encheres" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
        <div className="container mx-auto px-4 py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-6">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">Enchères</span>
          </nav>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#F0A22E]/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
              <Gavel className="w-5 h-5 text-[#F0A22E]" />
              <span className="text-sm font-medium text-[#F0A22E]">Vente par courtage aux enchères</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-testid="encheres-hub-title">
              Matériel géotechnique aux enchères
            </h1>

            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl">
              Foreuses, sondeuses, enregistreurs de paramètres de forage, pompes d&apos;injection 
              et outillage vendus à la meilleure offre. Inscription gratuite.
            </p>

            {/* Legal Notice - Always visible */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-8 border border-white/20">
              <p className="text-sm text-gray-200">
                <strong className="text-white">Vente par courtage aux enchères — vente de gré à gré.</strong><br />
                Il ne s&apos;agit pas d&apos;une vente aux enchères publiques. Le vendeur reste libre 
                du choix de l&apos;acquéreur et peut refuser l&apos;ensemble des offres.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link to="/encheres/connexion">
                <Button size="lg" className="bg-[#F0A22E] hover:bg-[#d89426] text-white">
                  <Users className="w-5 h-5 mr-2" />
                  Participer aux enchères
                </Button>
              </Link>
              <Link to="/encheres/devenir-vendeur">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Vendre du matériel
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A2540]">Inscription gratuite</h3>
                <p className="text-sm text-gray-600">Créez votre compte acheteur sans frais</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A2540]">Dossiers vérifiés</h3>
                <p className="text-sm text-gray-600">Tous les participants sont qualifiés</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-[#F0A22E]/20 p-2 rounded-lg">
                <Gavel className="w-5 h-5 text-[#F0A22E]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A2540]">Commission au succès</h3>
                <p className="text-sm text-gray-600">Vendeurs : payez uniquement si vente</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A2540]">Transparence totale</h3>
                <p className="text-sm text-gray-600">Historique des offres en temps réel</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Current Vacations */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-6">
          Ventes en cours et à venir
        </h2>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#F0A22E]"></div>
          </div>
        ) : vacations.length === 0 ? (
          <Card className="bg-gray-50 border-dashed">
            <CardContent className="py-12 text-center">
              <Gavel className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Aucune vente programmée
              </h3>
              <p className="text-gray-500 mb-4">
                Revenez bientôt pour découvrir nos prochaines ventes de matériel géotechnique.
              </p>
              <Link to="/encheres/devenir-vendeur">
                <Button variant="outline">
                  Proposer du matériel à la vente
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="encheres-vacations-grid">
            {vacations.map(vacation => {
              const daysUntil = getDaysUntil(vacation.date_cloture_inscriptions);
              const imageUrl = vacation.image_url 
                ? (vacation.image_url.startsWith('http') 
                    ? vacation.image_url 
                    : `${API.replace('/api', '')}${vacation.image_url}`)
                : null;
              
              return (
                <Link key={vacation.id} to={`/encheres/${vacation.slug}`} data-testid={`vacation-card-${vacation.slug}`}>
                  <Card className="h-full hover:shadow-lg transition-shadow group overflow-hidden" data-testid="vacation-card">
                    {/* Photo de couverture */}
                    {imageUrl && (
                      <div className="relative h-40 overflow-hidden">
                        <img 
                          src={imageUrl}
                          alt={vacation.titre_fr}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                        <div className="absolute top-3 left-3">
                          {getStatusBadge(vacation.statut)}
                        </div>
                        {vacation.statut === 'annoncee' && daysUntil !== null && daysUntil > 0 && (
                          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-sm font-medium text-[#0A2540]">
                            J-{daysUntil}
                          </div>
                        )}
                      </div>
                    )}
                    <CardHeader className={imageUrl ? "pb-2 pt-3" : "pb-2"}>
                      {!imageUrl && (
                        <div className="flex justify-between items-start mb-2">
                          {getStatusBadge(vacation.statut)}
                          {vacation.statut === 'annoncee' && daysUntil !== null && daysUntil > 0 && (
                            <span className="text-sm text-gray-500">
                              J-{daysUntil}
                            </span>
                          )}
                        </div>
                      )}
                      <CardTitle className="text-lg group-hover:text-[#F0A22E] transition-colors">
                        {vacation.titre_fr}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {vacation.description_fr && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                          {vacation.description_fr}
                        </p>
                      )}
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4 text-[#F0A22E]" />
                          <span>
                            {vacation.statut === 'annoncee' 
                              ? `Inscriptions jusqu'au ${formatDate(vacation.date_cloture_inscriptions)}`
                              : `Vente le ${formatDate(vacation.date_ouverture_vente)}`
                            }
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Gavel className="w-4 h-4 text-[#F0A22E]" />
                          <span>{vacation.nb_lots} lot{vacation.nb_lots > 1 ? 's' : ''}</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t flex items-center justify-between">
                        <span className="text-sm text-[#F0A22E] font-medium group-hover:underline">
                          Voir les lots
                        </span>
                        <ArrowRight className="w-4 h-4 text-[#F0A22E] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Past Vacations - Reference prices */}
      {pastVacations.length > 0 && (
        <div className="bg-gray-100 py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-bold text-[#0A2540] mb-6">
              Ventes terminées — Références de prix
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastVacations.map(vacation => (
                <Link key={vacation.id} to={`/encheres/${vacation.slug}`}>
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="py-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-[#0A2540]">{vacation.titre_fr}</h3>
                          <p className="text-sm text-gray-500">
                            {vacation.nb_lots} lot{vacation.nb_lots > 1 ? 's' : ''} • {formatDate(vacation.date_cloture_vente)}
                          </p>
                        </div>
                        <Badge variant="outline">Terminée</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Seller CTA */}
      <div className="bg-gradient-to-r from-[#F0A22E] to-[#d89426] py-12">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Vous avez du matériel à vendre ?
          </h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            Dépôt gratuit, aucun droit d&apos;entrée. Nous ne sommes rémunérés que si votre lot trouve preneur.
          </p>
          <Link to="/encheres/devenir-vendeur">
            <Button size="lg" className="bg-white text-[#F0A22E] hover:bg-gray-100">
              Demander un accès vendeur
            </Button>
          </Link>
        </div>
      </div>

      {/* SEO Content Block */}
      <div className="bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-2xl font-bold text-[#0A2540] mb-6">
              Comment fonctionne la vente par courtage aux enchères ?
            </h2>
            
            <div className="space-y-4 text-gray-600">
              <p>
                GEOTS organise des ventes par courtage aux enchères pour le matériel de géotechnique 
                et de fondations spéciales. Ce mode de vente permet aux professionnels du secteur 
                d&apos;acquérir des foreuses, sondeuses, enregistreurs de paramètres de forage et outillage 
                dans des conditions transparentes.
              </p>
              
              <h3 className="text-xl font-semibold text-[#0A2540] mt-6">Pour les acheteurs</h3>
              <p>
                L&apos;inscription est gratuite et ouverte à tous les professionnels. Après validation de votre 
                dossier, vous pouvez enchérir sur les lots proposés. Vous suivez en temps réel l&apos;évolution 
                des offres et recevez une notification si vous êtes dépassé.
              </p>
              
              <h3 className="text-xl font-semibold text-[#0A2540] mt-6">Pour les vendeurs</h3>
              <p>
                Le dépôt de lots est gratuit : aucun droit d&apos;entrée ni frais de dossier. La commission 
                n&apos;est due qu&apos;en cas de vente effective. Vous restez maître de la décision finale : 
                conformément à l&apos;article L.321-3 du Code de commerce, il s&apos;agit d&apos;une vente de gré à gré 
                et vous n&apos;êtes pas tenu d&apos;accepter l&apos;offre la plus élevée.
              </p>
              
              <h3 className="text-xl font-semibold text-[#0A2540] mt-6">Matériels proposés</h3>
              <p>
                Nos ventes portent principalement sur : foreuses géotechniques, sondeuses hydrauliques, 
                enregistreurs de paramètres de forage (type Jean Lutz, BAP), pompes d&apos;injection, 
                centrales de coulis, tubages, outils de forage et équipements d&apos;instrumentation.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Internal Links to Guides */}
      <div className="bg-gray-50 py-12 border-t">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#0A2540] mb-6 text-center">
            Nos guides pour bien choisir
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/guides/foreuse-geotechnique" className="group">
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <h3 className="font-semibold text-[#0A2540] group-hover:text-[#F0A22E] transition-colors">
                    Guide : Foreuse géotechnique
                  </h3>
                  <p className="text-sm text-gray-500">Comment choisir sa foreuse</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/guides/enregistreur-parametres-forage" className="group">
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <h3 className="font-semibold text-[#0A2540] group-hover:text-[#F0A22E] transition-colors">
                    Guide : Enregistreur de forage
                  </h3>
                  <p className="text-sm text-gray-500">Diagraphie et paramètres</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/guides/foreuse-occasion-micropieux" className="group">
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <h3 className="font-semibold text-[#0A2540] group-hover:text-[#F0A22E] transition-colors">
                    Guide : Foreuse d&apos;occasion
                  </h3>
                  <p className="text-sm text-gray-500">Acheter une foreuse d&apos;occasion</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </div>

      {/* Conditions Link */}
      <div className="bg-[#0A2540] py-8">
        <div className="container mx-auto px-4 text-center">
          <Link to="/encheres/conditions" className="text-gray-300 hover:text-white text-sm underline">
            Consulter les conditions générales de vente par courtage aux enchères
          </Link>
        </div>
      </div>
    </div>
  );
}
